import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import i18n from '../../i18n';
import { ReactComponent as MenuBookIcon } from '../../static/images/svg/material-symbols_menu-book-outline.svg';
import { ReactComponent as CloseFillIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import DialogComponent from '../../component/dialog/dialog.component';

type MenuStoreCommonDialogProps = {
    /** 弹窗标题。 */
    title: string;
    /** 关闭弹窗回调。 */
    onClose: () => void;
    /** 弹窗主体内容。 */
    children: React.ReactNode;
    /** 菜品详情等大表单：MUI Paper 限宽并视口居中（与 `sx` 内 Paper 规则一致，避免被 `width:auto` 扯满屏）。 */
    dishDetailLayout?: boolean;
    /** 弹窗容器额外类名。 */
    dialogClassName?: string;
    /** 是否隐藏标题左侧图标。 */
    hideTitleIcon?: boolean;
    /** 标题左侧自定义前缀。 */
    titlePrefix?: React.ReactNode;
    /** 自定义底部区域；传入后不再渲染默认关闭按钮。 */
    footer?: React.ReactNode;
    /** 是否隐藏默认关闭按钮（当 footer 为空时生效）。 */
    hideBottomClose?: boolean;
};

/**
 * 菜单商店公共弹窗壳（统一遮罩、标题与关闭按钮）。
 */
export function MenuStoreCommonDialog(props: MenuStoreCommonDialogProps): React.ReactElement {
    /** 组件属性。 */
    const { title, onClose, children, dishDetailLayout, dialogClassName, hideTitleIcon, titlePrefix, footer, hideBottomClose } = props;
    return (
        <DialogComponent
            className={dishDetailLayout ? 'menu-store-common-dialog--dish-detail' : undefined}
            open
            onClose={onClose}
            defaultHeaderProps={{ isHiddenCloseIcon: true }}
            sx={[
                {
                    '.MuiBackdrop-root': {
                        backgroundColor: 'var(--overlay-800-a25)',
                    },
                    '.MuiDialogTitle-root': {
                        display: 'none',
                    },
                    '.MuiDialog-container > .MuiPaper-root': {
                        background: 'transparent',
                        border: 0,
                        boxShadow: 'none',
                        borderRadius: 0,
                        overflow: 'visible',
                        ...(dishDetailLayout
                            ? {
                                  width: 'min(1120px, calc(100vw - 32px))',
                                  maxWidth: 'min(1120px, calc(100vw - 32px))',
                                  margin: '16px',
                                  boxSizing: 'border-box',
                              }
                            : {
                                  width: 'auto',
                                  maxWidth: 'none',
                              }),
                    },
                    '.MuiDialogContent-root': {
                        padding: '0 !important',
                        overflow: dishDetailLayout ? 'auto' : 'visible',
                    },
                    '.dialog-footer': {
                        display: 'none',
                    },
                },
                ...(dishDetailLayout
                    ? [
                          {
                              '.MuiDialog-container': {
                                  justifyContent: 'center',
                                  alignItems: 'center',
                              },
                          },
                      ]
                    : []),
            ]}
            dialogContentStyle={{ paddingTop: 0 }}
        >
            <Box role="dialog" aria-modal="true" aria-labelledby="menu-store-common-dialog-title">
                <Box className={['menu-store-activate-dialog', dialogClassName ?? ''].filter(Boolean).join(' ')}>
                    <Box className="menu-store-activate-dialog__title-row">
                        <Box className="menu-store-activate-dialog__title-left">
                            {titlePrefix ?? (!hideTitleIcon ? <MenuBookIcon className="menu-store-activate-dialog__book-icon" aria-hidden /> : null)}
                            <Typography id="menu-store-common-dialog-title" variant="h4" component="h2" className="menu-store-activate-dialog__title-text">
                                {title}
                            </Typography>
                        </Box>
                        <IconButton type="button" className="menu-store-activate-dialog__close" aria-label={i18n.t('menu_store_activate_close_aria')} onClick={onClose} size="small">
                            <CloseFillIcon width={40} height={40} aria-hidden />
                        </IconButton>
                    </Box>
                    <Box className="menu-store-activate-dialog__body">{children}</Box>
                    {footer ?? (!hideBottomClose ? (
                        <Button className="menu-store-activate-dialog__submit" variant="contained" disableElevation fullWidth onClick={onClose}>
                            {i18n.t('menu_store_activate_close_aria')}
                        </Button>
                    ) : null)}
                </Box>
            </Box>
        </DialogComponent>
    );
}
